import { useCallback, useEffect, useReducer } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { WizardMachine } from '@/components/WizardParts'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { SelectRestorePointPane } from '@/screens/shared/restore-panes/SelectRestorePointPane'
import { SelectRepositoriesPane } from '@/screens/shared/restore-panes/SelectRepositoriesPane'
import { SelectRefPane } from '@/screens/shared/restore-panes/SelectRefPane'
import { TargetObjectTypesPane } from '@/screens/shared/restore-panes/TargetObjectTypesPane'
import { TargetPullRequestsPane } from '@/screens/shared/restore-panes/TargetPullRequestsPane'
import { TargetWorkspaceHooksPane } from '@/screens/shared/restore-panes/TargetWorkspaceHooksPane'
import { PlanPane } from '@/screens/shared/restore-panes/PlanPane'
import { OrderPane } from '@/screens/shared/restore-panes/OrderPane'
import { initialWizardStage, wizardReducer } from './types'
import { buildRestoreInput } from './buildRestoreInput'
import type { RestoreWizardConfig, WizardStage } from './types'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type WorkspaceSummary = components['schemas']['WorkspaceSummary']
type PlanStage = Extract<WizardStage, { stage: 'plan' }>
type RemapReturnState = { readonly resumeAtPlan: PlanStage } | null | undefined

const PANE_HEADING: Record<WizardStage['stage'], string> = {
  select: 'What are you restoring, and from when?',
  repositories: 'What does this restore cover?',
  selectRef: 'Which ref?',
  targets: 'Where does it go, and how much of it?',
  plan: 'What this restore will do',
  order: 'Write order',
}

/**
 * `_family-wizard.md`'s shared engine, instantiated once per `RestoreWizardConfig` — the eight
 * families (`repo`/`access`/`project`/`ci`/`settings`/`ref`/`review`/`wshook`) are eight call sites
 * of this one component, not eight copies of it, the same relationship the reports family (Pass 8)
 * will have to its own templated route.
 *
 * **Cross-workspace restore (W155, closing the W146 gap this file's own comment used to name).**
 * The three `targets`-role panes now offer a real destination-workspace picker
 * (`DestinationWorkspacePicker`); choosing one other than the source navigates OUT to `/remap`
 * (`v-remap`'s own screen, unreachable from any live flow until this pass) with a real
 * `RestoreInput` and this stage's own shape as route state, rather than dispatching straight to
 * `plan` — `restoreProposePrincipalMap` needs a full `RestoreInput`, which the reducer alone
 * cannot produce mid-flight. `v-remap`'s own "Continue" navigates back to this exact route with
 * `{ resumeAtPlan: <the same stage, principal map filled in> }`, and the `useEffect` below resumes
 * the reducer directly at `plan` — a route round-trip rather than an embedded pane, because
 * `v-remap` is its own registered, spec'd unit (`design/27-ui-surface-and-navigation.md` lists
 * `remap` under "Restore", not under any one wizard family) and reusing it as a route is what lets
 * every one of the eight machines share it without eight copies of its own review UI.
 */
export function RestoreWizardLayer({ config }: { config: RestoreWizardConfig }) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const navigate = useNavigate()
  const location = useLocation()
  const reducer = useCallback((state: Parameters<typeof wizardReducer>[1], action: Parameters<typeof wizardReducer>[2]) => wizardReducer(config, state, action), [config])
  const [stage, dispatch] = useReducer(reducer, initialWizardStage)

  useEffect(() => {
    const resume = location.state as RemapReturnState
    if (resume?.resumeAtPlan) {
      dispatch({ type: 'RESUME_AT_PLAN', stage: resume.resumeAtPlan })
      navigate(location.pathname, { replace: true, state: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  function handleTargetsNext(args: { objectTypes?: readonly string[]; destinationWorkspaceId?: string; destinationLabel: string }) {
    if (stage.stage !== 'targets') return
    if (args.destinationWorkspaceId && args.destinationWorkspaceId !== workspaceId) {
      const stageAtPlan: PlanStage = {
        stage: 'plan',
        restorePoint: stage.restorePoint,
        repositoryIds: stage.repositoryIds,
        projectKeys: stage.projectKeys,
        repositoryId: stage.repositoryId,
        ref: stage.ref,
        objectTypes: args.objectTypes,
        destinationWorkspaceId: args.destinationWorkspaceId,
        destinationLabel: args.destinationLabel,
      }
      const restoreInput = buildRestoreInput(config, stageAtPlan)
      navigate('/remap', { state: { restoreInput, returnPath: location.pathname, plan: stageAtPlan } })
      return
    }
    dispatch({ type: 'TARGETS_PICKED', ...args })
  }

  const workspace = useQuery({
    queryKey: ['workspace-name', orgId, workspaceId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — a mount-time
    // race (`ShellLayer`'s `setActiveOrg`/workspace-selection effects run after the first render)
    // can leave `orgId` null on this layer's own first render; unguarded, this fires with a null
    // org id and openapi-fetch leaves the unmatched `{org_id}` placeholder literally in the URL.
    enabled: Boolean(orgId),
    queryFn: async (): Promise<WorkspaceSummary | undefined> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items as unknown as WorkspaceSummary[]).find((w) => w.workspace_id === workspaceId)
    },
  })
  const destinationWorkspaceName = workspace.data?.name ?? workspaceId

  return (
    <WizardMachine>
      {/* Every pane below owns its own top-level shape — `select`/`selectRef`/`targets` panes each
       * lay out their own `wizsplit` (or, for `selectRef`, the single-column `onecol` variant) or
       * `grid2` (`plan`) or no split at all (`order`), matching what `Prototype_v2.html` actually
       * does for that role. This layer used to wrap ALL of them in one blanket `wizsplit`/420px
       * `WizardCard` — built for the small field-only panes and never revisited once every pane's
       * own full-width tables/panels were added, which squeezed every one of them (not just
       * `select`, fixed first) sideways into a 420px column. */}
      {stage.stage === 'select' && (
        <SelectRestorePointPane
          config={config}
          heading={PANE_HEADING.select}
          onCancel={() => history.back()}
          onNext={(args) => dispatch({ type: 'SELECT_COMPLETE', ...args })}
        />
      )}

      {stage.stage === 'repositories' && (
        <SelectRepositoriesPane
          config={config}
          heading={PANE_HEADING.repositories}
          restorePoint={stage.restorePoint}
          onNext={(args) => dispatch({ type: 'REPOSITORIES_COMPLETE', ...args })}
          onBack={() => dispatch({ type: 'BACK' })}
        />
      )}

      {stage.stage === 'selectRef' && (
        <SelectRefPane
          config={config}
          heading={PANE_HEADING.selectRef}
          jobId={stage.restorePoint.data_snapshot.job_id}
          repositoryId={stage.repositoryId}
          onNext={(ref) => dispatch({ type: 'REF_PICKED', ref })}
          onBack={() => dispatch({ type: 'BACK' })}
        />
      )}

      {stage.stage === 'targets' && config.instances?.probeCapability === 'pull-request-branches' && (
        <TargetPullRequestsPane
          config={config}
          heading={PANE_HEADING.targets}
          jobId={stage.restorePoint.data_snapshot.job_id}
          repositoryId={stage.repositoryIds?.[0] ?? ''}
          destinationWorkspaceName={destinationWorkspaceName}
          onNext={handleTargetsNext}
          onBack={() => dispatch({ type: 'BACK' })}
        />
      )}

      {stage.stage === 'targets' && config.instances?.probeCapability === 'workspace-hook' && (
        <TargetWorkspaceHooksPane
          config={config}
          heading={PANE_HEADING.targets}
          jobId={stage.restorePoint.data_snapshot.job_id}
          destinationWorkspaceName={destinationWorkspaceName}
          onNext={handleTargetsNext}
          onBack={() => dispatch({ type: 'BACK' })}
        />
      )}

      {stage.stage === 'targets' && !config.instances && (
        <TargetObjectTypesPane
          config={config}
          heading={PANE_HEADING.targets}
          destinationWorkspaceName={destinationWorkspaceName}
          onNext={handleTargetsNext}
          onBack={() => dispatch({ type: 'BACK' })}
        />
      )}

      {stage.stage === 'plan' && <PlanPane config={config} stage={stage} onNext={() => dispatch({ type: 'PLAN_ACCEPTED' })} onBack={() => dispatch({ type: 'BACK' })} />}

      {stage.stage === 'order' && <OrderPane config={config} heading={PANE_HEADING.order} stage={stage} onBack={() => dispatch({ type: 'BACK' })} />}
    </WizardMachine>
  )
}
